/* eslint-disable no-empty-function */

import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { ErrorViewModel } from 'stageclip-common';

import * as SessionHelper from '../../helpers/sessionHelper';
import * as EntityNormalizers from '../normalizers/entityNormalizers';

export abstract class BaseApiRequest<ResponseType, ReturnType extends EntityNormalizers.NormalizedEntity<any>> {
  private static buildApiUrl(path: string, queryParameters: any) {
    let query = '';

    if (queryParameters) {
      const queryParts = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const key in queryParameters) {
        if (queryParameters.hasOwnProperty(key)) {
          let parameterValue = queryParameters[key];

          if (Array.isArray(parameterValue)) {
            parameterValue = `[${parameterValue.map(e => `"${e}"`).join(',')}]`;
          }
          queryParts.push(`${key}=${parameterValue}`);
        }
      }
      query += `?${queryParts.join('&')}`;
    }

    return `${process.env.REACT_APP_API_URL}${path}${query}`;
  }

  private static processError(error: any, response: Response): ErrorViewModel {
    const errorMessage =
      // eslint-disable-next-line no-nested-ternary
      response.status === 404 ? response.statusText : error.message ? error.message : 'An unknown error has occurred';

    return {
      errors: error.errors,
      message: errorMessage,
    };
  }

  public path: string;

  protected method = 'NOTSET';

  protected queryParameters: any;

  protected normalizeMethod: (r: any) => ReturnType;

  // eslint-disable-next-line no-useless-constructor
  protected constructor(
    public component: React.Component,
    public dispatch: Dispatch,
    protected shouldDispatchError: boolean = true,
  ) {}

  public async send(): Promise<ReturnType> {
    const httpResponse = await this.fetchRequest();

    // eslint-disable-next-line no-return-await
    return await this.processResponse(httpResponse);
  }

  protected async sendAndDispatch(actionCreator: (r: ReturnType) => AnyAction): Promise<void> {
    const result = await this.send();
    this.dispatch(actionCreator(result));
  }

  // noinspection JSMethodCanBeStatic
  protected generateHeaders(): Headers {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    this.addAuthentication(headers);

    return headers;
  }

  protected addAuthentication(headers: Headers) {
    headers.append('Authorization', `Bearer ${SessionHelper.getSessionToken()}`);
  }

  protected async fetchRequest(): Promise<Response> {
    // eslint-disable-next-line no-return-await
    return await fetch(this.buildApiUrl(), this.generateRequest());
  }

  protected generateRequest(): RequestInit {
    return {
      headers: this.generateHeaders(),
      method: this.method,
    };
  }

  protected buildApiUrl() {
    return BaseApiRequest.buildApiUrl(this.path, this.queryParameters);
  }

  protected async processResponse(response: Response): Promise<ReturnType> {
    let jsonResponse = {};
    try {
      jsonResponse = await response.json();
    } catch (e) {
      // Do nothing the response is probably empty
    }

    if (!response.ok) {
      throw BaseApiRequest.processError(jsonResponse, response);
    } else {
      try {
        return this.normalizeMethod(jsonResponse);
      } catch (e) {
        throw jsonResponse;
      }
    }
  }
}
